import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../hooks/useRedux';
import { fetchInvoiceEditData, setSelectedCustomer, createCustomer, updateCustomer } from '../features/customer/customer.slice';
import type { Customer } from '../features/customer/customer.service';
import LineButton from './line-button.component';
import AddCustomerModal, { EditCustomerModal } from './add-customer-modal';
import EditBillingAddressModal from './edit-billing-address-modal/edit-billing-address-modal.component';
import './customer-details.scss';
import { getItem } from '../helper/localstorage.helper';
import { ref } from 'process';
import { useTranslation } from 'react-i18next';
import useOutsideClick from '../hooks/useOutsideClick';

interface CustomerDetailsProps {
  customer?: {
    name: string;
    id: string;
    billingAddress: string;
  };
  onCustomerChange?: (customer: any) => void;
  onAddCustomer?: (customerData: any) => void;
  onEditAddress?: () => void;
  copiedInvoiceData?: any;  // NEW - Optional copied invoice data
  isEditModeInvoice?: boolean;  // NEW - Edit mode flag
  timesheetData?: any[];  // NEW - Timesheet data from Redux
}

const CustomerDetails: React.FC<CustomerDetailsProps> = ({
  customer,
  onCustomerChange,
  onAddCustomer,
  onEditAddress,
  copiedInvoiceData,
  isEditModeInvoice,
  timesheetData
}) => {
  const { t } = useTranslation();
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showEditBillingModal, setShowEditBillingModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditingYourReference, setIsEditingYourReference] = useState(false);
  const [isEditingCustomerReference, setIsEditingCustomerReference] = useState(false);
  const [yourReferenceValue, setYourReferenceValue] = useState('XXXXXXXX');
  const [customerReferenceValue, setCustomerReferenceValue] = useState('XXXXX XXXXXX');
  const [userID, setUserId] = useState<number | undefined>(undefined);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useOutsideClick(() => {
    setIsDropdownOpen(false);
  }
  ) as React.MutableRefObject<HTMLDivElement | null>;

  // Ref to track if invoice data has been fetched to prevent duplicate API calls
  const dataFetchedRef = useRef(false);
  
  // Ref to track latest customers list for polling mechanism
  const latestCustomersRef = useRef<Customer[]>([]);

  const getUserID = async () => {
    const userItem = getItem("user");
    const userId = userItem ? JSON.parse(userItem) : null;
    if (userId && userId.userData && userId.userData.userId) {
      setUserId(userId.userData.userId);
    } else {
      setUserId(1500);
    }
  }

  useEffect(() => {
    getUserID();
  }, []);


  const dispatch = useAppDispatch();
  const customerState = useAppSelector((state: any) => state?.customer || {});
  const { customers = [], loading = false, error = null, selectedCustomer } = customerState;
  
  // Check if we're in fresh creation mode
  const searchParams = new URLSearchParams(window.location.search);
  const hasEditParam = searchParams.get('edit') === 'true';
  const hasPreIdParam = searchParams.get('preId');
  const isTimesheetMode = searchParams.get('timesheet') === 'true';
  const hasInvoiceNumber = searchParams.get('invoiceNumber');
  const isFreshCreation = !hasEditParam && !hasPreIdParam && !isTimesheetMode && !hasInvoiceNumber;
  
  // Only use customer prop if not in fresh creation mode
  const currentCustomer = selectedCustomer || 
    (isFreshCreation ? null : customer) || 
    { name: t('SELECT_A_CUSTOMER'), id: '', billingAddress: '' };
  
  // Update ref whenever customers change
  useEffect(() => {
    latestCustomersRef.current = customers;
  }, [customers]);

  // Reset customer selection when selectedCustomer becomes null from Redux
  useEffect(() => {
    if (selectedCustomer === null && !copiedInvoiceData && !isEditModeInvoice) {
      // Ensure the component reflects the reset state
      if (onCustomerChange) {
        onCustomerChange(null);
      }
      // Also reset reference values
      setYourReferenceValue('XXXXXXXX');
      setCustomerReferenceValue('XXXXX XXXXXX');
    }
  }, [selectedCustomer, copiedInvoiceData, isEditModeInvoice, onCustomerChange]);

  // Handle copiedInvoiceData and auto-select customer
  useEffect(() => {
   
    // Check if we're in timesheet mode via URL parameter
    const searchParams = new URLSearchParams(window.location.search);
    const isTimesheetMode = searchParams.get('timesheet') === 'true';
    const hasEditParam = searchParams.get('edit') === 'true';
    const hasPreIdParam = searchParams.get('preId');
    const hasInvoiceNumber = searchParams.get('invoiceNumber');

    // Check if this is fresh creation (no special params)
    const isFreshCreation = !hasEditParam && !hasPreIdParam && !isTimesheetMode && !hasInvoiceNumber;

    // If fresh creation and no copied data, clear selection immediately
    if (isFreshCreation && !copiedInvoiceData) {
      dispatch(setSelectedCustomer(null));
      if (onCustomerChange) {
        onCustomerChange(null);
      }
      return; // Exit early
    }

    // Wait until customers are loaded and we're not in loading state
    if (!customers || customers.length === 0 || loading) {
      return;
    }

    // Handle timesheet data - auto-select customer from timesheet
    // Only use timesheetData if we're in timesheet mode (timesheet=true in URL)
    if (isTimesheetMode && timesheetData && timesheetData.length > 0 && timesheetData[0]?.customerId) {
      const timesheetCustomerId = timesheetData[0].customerId;
      
      const matchingCustomer = customers.find((c: Customer) => {
        const customerId = c.customerId || c.id;
        return customerId?.toString() === timesheetCustomerId?.toString();
      });

      if (matchingCustomer) {
        const customerId = matchingCustomer.customerId || matchingCustomer.id;
        handleCustomerSelect(customerId?.toString() || '');
      } else {
      }
      return; // Exit early after handling timesheet data
    }

    if (copiedInvoiceData && copiedInvoiceData.customer) {
      const customerFromInvoice = copiedInvoiceData.customer;
     

      const matchingCustomer = customers.find((c: Customer) => {
        const customerId = c.customerId || c.id;
        const matches = customerId?.toString() === customerFromInvoice.customerId?.toString() ||
          c.name?.toLowerCase() === customerFromInvoice.customerName?.toLowerCase();
        
       
        return matches;
      });

      if (matchingCustomer) {
        // Select the customer using existing handler
        const customerId = matchingCustomer.customerId || matchingCustomer.id;
        handleCustomerSelect(customerId?.toString() || '');
      } else {
        // If customer not found in list, set it directly (for display purposes)
        const customerData = {
          id: customerFromInvoice.customerId?.toString() || '',
          customerId: customerFromInvoice.customerId || 0,
          name: customerFromInvoice.customerName || customerFromInvoice.name || '',
          billingAddress: `${customerFromInvoice.addressLine1 || ''}\n${customerFromInvoice.city || ''}, ${customerFromInvoice.zipCode || ''}\n${customerFromInvoice.country || ''}`.trim(),
          email: customerFromInvoice.email || '', // Add customer email
          secondaryEmailAddress: customerFromInvoice.secondaryEmailAddress || '',
          paymentTerm: customerFromInvoice.paymentTerm, // Add paymentTerm for due date calculation
        };

        dispatch(setSelectedCustomer(customerData));

        if (onCustomerChange) {
          onCustomerChange(customerData);
        }
      }

      // Set reference values if available
      if (copiedInvoiceData.yourReference) {
        setYourReferenceValue(copiedInvoiceData.yourReference);
      }
      if (copiedInvoiceData.customerReference) {
        setCustomerReferenceValue(copiedInvoiceData.customerReference);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copiedInvoiceData, isEditModeInvoice, customers, loading, timesheetData]);

  useEffect(() => {
    if (userID && !dataFetchedRef.current) {
      dataFetchedRef.current = true;
      dispatch(fetchInvoiceEditData(userID));
    }
  }, [dispatch, userID]);

  const handleCustomerSelect = (customerId: string) => {
    // Use latestCustomersRef to get the most up-to-date customer list
    const currentCustomers = latestCustomersRef.current;
    
    const selected = currentCustomers.find((c: Customer) => {
      const customerIdToCheck = c.customerId || c.id;
      return customerIdToCheck?.toString() === customerId;
    });
   
    console.log('=== Customer Selection Debug ===');
    console.log('Selected customer from API:', selected);
    console.log('hasAnyInvoice from API:', selected?.hasAnyInvoice);
    console.log('================================');
   
    if (selected) {
      const customerData = {
        id: (selected.customerId || selected.id)?.toString() || '',
        customerId: selected.customerId || selected.id || 0,
        name: selected.name,
        billingAddress: selected.address || 'No address provided',
        email: selected.email || '', // Add customer email for API payload
        secondaryEmailAddress: selected.secondaryEmailAddress || '',
        accountType: selected.accountType, // Add accountType for RUT/ROT validation
        country: selected.country, // Add country for RUT/ROT validation
        paymentTerm: selected.paymentTerm, // Add paymentTerm for due date calculation
        hasAnyInvoice: selected.hasAnyInvoice, // Add hasAnyInvoice for warning condition
      };
      dispatch(setSelectedCustomer(customerData));

      // Update reference values from selected customer
      if (selected.reference) {
        setYourReferenceValue(selected.reference);
      }

      if (onCustomerChange) {
        onCustomerChange(customerData);
      }
     }
  };

  const handleAddCustomerClick = () => {
    setIsEditMode(false);
    setShowAddCustomerModal(true);
  };

  const handleEditCustomerClick = () => {
    setIsEditMode(true);
    setShowEditCustomerModal(true);
  };

  const handleEditAddressClick = () => {
    setShowEditBillingModal(true);
  };

  const getCurrentCustomerBillingData = () => {
    if (!selectedCustomer?.id) return undefined;
    const fullCustomerData = customers.find((c: Customer) => {
      const customerIdToCheck = c.customerId || c.id;
      return customerIdToCheck?.toString() === selectedCustomer.id;
    });

    if (fullCustomerData) {
      const mapCountryToCode = (country: string) => {
        const countryMappings: { [key: string]: string } = {
          'Sweden': 'SE',
          'Norway': 'NO',
          'Denmark': 'DK',
          'Finland': 'FI',
          'United States': 'US',
          'USA': 'US',
          'United Kingdom': 'GB',
          'Germany': 'DE',
          'Canada': 'CA'
        };
        if (Object.values(countryMappings).includes(country)) {
          return country;
        }
        return countryMappings[country] || country;
      };
      return {
        country: mapCountryToCode(fullCustomerData.country || ''),
        stateCounty: '',
        city: fullCustomerData.city || '',
        addressLine: fullCustomerData.address || '',
        zipCode: fullCustomerData.zipCode || ''
      };
    }

    return;
  };

  const handleEditBillingModalClose = () => {
    setShowEditBillingModal(false);
  };

  const handleBillingAddressSave = (billingData: any) => {
    if (onEditAddress) {
      onEditAddress();
    }
    setShowEditBillingModal(false);
  };

  const handleEditCustomerReferenceClick = () => {
    setIsEditingCustomerReference(true);
  };

  const handleYourReferenceSave = () => {
    setIsEditingYourReference(false);
  };

  const handleCustomerReferenceSave = () => {
    setIsEditingCustomerReference(false);
  };

  const handleYourReferenceKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleYourReferenceSave();
    } else if (e.key === 'Escape') {
      setYourReferenceValue('XXXXXXXX'); // Reset to original value
      setIsEditingYourReference(false);
    }
  };

  const handleCustomerReferenceKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCustomerReferenceSave();
    } else if (e.key === 'Escape') {
      setCustomerReferenceValue('XXXXX XXXXXX'); // Reset to original value
      setIsEditingCustomerReference(false);
    }
  };

  // Update reference values when selected customer changes
  useEffect(() => {
    if (selectedCustomer ) {
      const fullCustomerData = customers.find((c: Customer) => {
        const customerIdToCheck = c.customerId || c.id;
        return customerIdToCheck?.toString() === selectedCustomer.id;
      });

      if (fullCustomerData) {
        // Update Your Reference from customer data
        if (fullCustomerData.reference) {
          setYourReferenceValue(fullCustomerData.reference);
        }
      }
    }
  }, [selectedCustomer, customers]);


  return (
    <Card className="invoice-card mb-4 customer-details">
      <Card.Header className="card-header d-flex justify-content-between align-items-center">
        <Card.Title as="h3" className="mb-0">{t('CUSTOMER_DETAILS')}</Card.Title>
        {selectedCustomer  && (
          <button
            onClick={handleEditCustomerClick}
            className="edit-customer-btn"
            // disabled={isEditModeInvoice}
          >
            {t('EDIT_CUSTOMER')}
          </button>
        )}
      </Card.Header>
      <Card.Body className="card-body" style={{ padding: '16px 0px' }}>
        <Row className="customer-details-content g-4">
          <Col lg={8} className="customer-selection">

            <label className="input-label">{t('SELECT_CUSTOMER')}</label>
            <div className="custom-dropdown" ref={dropdownRef}>
              <div
                className={`dropdown-trigger ${isDropdownOpen ? 'open' : ''} ${isEditModeInvoice ? 'disabled' : ''}`}
                onClick={() => !isEditModeInvoice && setIsDropdownOpen(!isDropdownOpen)}
              >
                <svg className="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="2" />
                  <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2" />
                </svg>
                <span className="dropdown-value">
                  {loading ? t('LOADING_CUSTOMERS') : (currentCustomer.name || t('SELECT_A_CUSTOMER'))}
                </span>
                <svg
                  className={`dropdown-arrow ${isDropdownOpen ? 'rotated' : ''}`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {isDropdownOpen && !loading && (
                <div className="dropdown-list">
                  <div className="dropdown-content">
                    {customers.map((cust: Customer) => {
                      const customerId = cust.customerId || cust.id;
                      return (
                        <div
                          key={customerId}
                          className={`list-item ${currentCustomer.id === customerId?.toString() ? 'selected' : ''}`}
                          onClick={() => {
                            handleCustomerSelect(customerId?.toString() || '');
                            setIsDropdownOpen(false);
                          }}
                        >
                          <div className="text-input">
                            <div className="paragraph-container">
                              <p className="option-text">{cust.name}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <LineButton text={t('ADD_NEW_CUSTOMER')} onClick={handleAddCustomerClick} disabled={isEditModeInvoice} />
          </Col>
          <Col lg={4} className="billing-address">
            <h4>{t('CUSTOMER_ADDRESS')}</h4>
            <div className="address-text">
              {currentCustomer.billingAddress ? (
                currentCustomer.billingAddress.split('\n').map((line: string, index: number) => (
                  <span key={index}>
                    {line}
                    {index < currentCustomer.billingAddress.split('\n').length - 1 && <br />}
                  </span>
                ))
              ) : (
                <span className="text-muted">{t('NO_BILLING_ADDRESS_AVAILABLE')}</span>
              )}
              <Button variant="link" className="edit-address-btn" onClick={handleEditAddressClick} disabled={isEditModeInvoice}> {t('EDIT')}</Button>
            </div>
          </Col>
        </Row>
        <div className="customer-reference-section">
          <div className="customer-id-badge">
            <span>{t('CUSTOMER_ID')} : {currentCustomer.id || t('NOT_SELECTED')}</span>
          </div>
          <div className="reference-divider"></div>
          <div className="reference-numbers">
            {/* <div className={`reference-item ${isEditingYourReference ? 'editing' : ''}`}>
              {!isEditingYourReference && (
                <Button
                  variant="link"
                  className="edit-icon-btn p-0"
                  onClick={handleEditYourReferenceClick}
                  disabled={isEditModeInvoice}
                >
                  <svg className="edit-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89783 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="#414651" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Button>
              )}
              {isEditingYourReference ? (
                <div className="inline-edit-container">
                  <input
                    type="text"
                    value={yourReferenceValue}
                    onChange={(e) => setYourReferenceValue(e.target.value)}
                    onKeyDown={handleYourReferenceKeyPress}
                    autoFocus
                    className="inline-edit-input"
                    placeholder={t('ENTER_YOUR_REFERENCE_NO')}
                  />
                  <Button
                    onClick={handleYourReferenceSave}
                    className="inline-edit-save-btn"
                  >
                    {t('SAVE')}
                  </Button>
                </div>
              ) : (
                <span>{t('YOUR_REFERENCE')}: {yourReferenceValue}</span>
              )}
            </div> */}
            <div className={`reference-item ${isEditingCustomerReference ? 'editing' : ''}`}>
              {!isEditingCustomerReference && (
                <Button
                  variant="link"
                  className="edit-icon-btn p-0"
                  onClick={handleEditCustomerReferenceClick}
                  disabled={isEditModeInvoice}
                >
                  <svg className="edit-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89783 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="#414651" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Button>
              )}
              {isEditingCustomerReference ? (
                <div className="inline-edit-container">
                  <input
                    type="text"
                    value={customerReferenceValue}
                    onChange={(e) => setCustomerReferenceValue(e.target.value)}
                    onKeyDown={(e) => {
                      // Block non-Latin characters
                      const allowedCharacters = /^[a-zA-Z0-9\s,.\-/']$/;
                      if (
                        e.key !== 'Backspace' &&
                        !allowedCharacters.test(e.key) &&
                        !['ArrowLeft', 'ArrowRight', 'Tab', 'Delete', 'Control', 'v', 'Meta', 'c', 'x', 'a', 'Enter', 'Escape'].includes(e.key) &&
                        !(e.ctrlKey || e.metaKey)
                      ) {
                        e.preventDefault();
                        return;
                      }
                      // Handle Enter and Escape keys
                      handleCustomerReferenceKeyPress(e);
                    }}
                    autoFocus
                    className="inline-edit-input"
                    placeholder={t('ENTER_CUSTOMER_REFERENCE_NAME')}
                  />
                  <Button
                    onClick={handleCustomerReferenceSave}
                    className="inline-edit-save-btn"
                  >
                    {t('SAVE')}
                  </Button>
                </div>
              ) : (
                <span>{t('CUSTOMER_REFERENCE_NAME')}: {yourReferenceValue}</span>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
      <AddCustomerModal
        show={showAddCustomerModal}
        onHide={() => setShowAddCustomerModal(false)}
        onSave={(customerData) => { 
          setShowAddCustomerModal(false);
          
          // Extract customer identifiers from the form data
          const customerName = customerData?.customerName || '';
          const customerEmail = customerData?.contactEmail || '';
          const customerSSN = customerData?.ssn || '';
          
          // Polling mechanism to wait for customer to appear in Redux state
          const maxRetries = 50; // Maximum 50 retries (25 seconds total)
          const retryInterval = 500; // Check every 500ms
          let retryCount = 0;
          
          const pollForNewCustomer = () => {
            retryCount++;
            // Get latest customers from ref (not closure)
            const currentCustomers = latestCustomersRef.current;
            
            
            // Try to find the newly created customer by name, email, or SSN
            const foundCustomer = currentCustomers.find((c: Customer) => {
              const nameMatch = c.name?.toLowerCase() === customerName?.toLowerCase();
              const emailMatch = c.email?.toLowerCase() === customerEmail?.toLowerCase();
              const ssnMatch = customerSSN && c.ssn === customerSSN;
              
              return nameMatch || emailMatch || ssnMatch;
            });
            
            if (foundCustomer) {
             
              const customerId = foundCustomer.customerId || foundCustomer.id;
              
              // Verify and select the customer
              const attemptSelection = (attemptsLeft = 3) => {
                // Re-fetch to ensure we have latest state
                const latestCustomers = latestCustomersRef.current;
                const selected = latestCustomers.find((c: Customer) => {
                  const customerIdToCheck = c.customerId || c.id;
                  return customerIdToCheck?.toString() === customerId?.toString();
                });
                
                if (selected) {
                  handleCustomerSelect(customerId?.toString() || '');
                } else if (attemptsLeft > 0) {
                  setTimeout(() => attemptSelection(attemptsLeft - 1), 500);
                }
              };
              
              attemptSelection();
            } else if (retryCount >= maxRetries) {
             
              
              // Last resort: try to select the last customer in the list
              if (currentCustomers.length > 0) {
                const lastCustomer = currentCustomers[currentCustomers.length - 1];
                const lastCustomerId = lastCustomer.customerId || lastCustomer.id;
                handleCustomerSelect(lastCustomerId?.toString() || '');
              }
            } else {
              // Customer not found yet, retry after interval
              setTimeout(pollForNewCustomer, retryInterval);
            }
          };
          
         setTimeout(pollForNewCustomer, 1000);
        }}
      />
      {showEditCustomerModal && (
        <EditCustomerModal
          show={showEditCustomerModal}
          onHide={() => {
            setShowEditCustomerModal(false);
            setSelectedCustomer(null);
          }}
          onSave={(customerData) => {
            setShowEditCustomerModal(false);
            // Auto-select the updated customer after API refresh completes
            setTimeout(() => {
              if (customerData?.customerId) {
                handleCustomerSelect(customerData.customerId.toString());
              }
            }, 500);
          }}
          selectedCustomer={selectedCustomer?.id}
        />
      )}

      <EditBillingAddressModal
        show={showEditBillingModal}
        onHide={handleEditBillingModalClose}
        onSave={handleBillingAddressSave}
        initialData={getCurrentCustomerBillingData()}
        customerId={selectedCustomer?.id ? parseInt(selectedCustomer.id) : undefined}
      />
    </Card>
  );
};

export default CustomerDetails;